import { Injectable } from '@angular/core';
import { KeycloakService } from 'keycloak-angular';
import Keycloak from 'keycloak-js';
import {
  GroupControllerService,
  KeycloakGroupSummaryResponse,
} from '@mobiletract/auth-service-angular-client';
import { Observable, of } from 'rxjs';
import { catchError, map, shareReplay, take, tap } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class PackageDisplayService {

  private displayNameMap: Record<string, string> = {};
  private load$: Observable<KeycloakGroupSummaryResponse[]> | null = null;

  constructor(
    private readonly keycloakService: KeycloakService,
    private readonly groupControllerService: GroupControllerService
  ) {}

  private get keycloak(): Keycloak {
    return this.keycloakService.getKeycloakInstance();
  }

  isLoggedIn(): boolean {
    return this.keycloak.authenticated ?? false;
  }

  getPackageDisplayName(): string {
    return this.resolveDisplayName();
  }

  getHasGroups$(): Observable<boolean> {
    if (Object.keys(this.displayNameMap).length > 0) {
      return of(true);
    }
    if (!this.isLoggedIn()) {
      return of(false);
    }
    return this.ensureGroupsLoaded().pipe(
      map((response) => (response ?? []).length > 0)
    );
  }

  getPackageDisplayName$(): Observable<string | null> {
    if (!this.isLoggedIn()) {
      return of(null);
    }
    if (Object.keys(this.displayNameMap).length > 0) {
      return of(this.resolveDisplayName());
    }
    return this.ensureGroupsLoaded().pipe(
      map(() => this.resolveDisplayName())
    );
  }

  login(): void {
    this.keycloak.login({
      redirectUri: window.location.href,
    });
  }

  private ensureGroupsLoaded(): Observable<KeycloakGroupSummaryResponse[]> {
    if (!this.load$) {
      this.load$ = this.groupControllerService.getGroups1('body').pipe(
        take(1),
        tap((response) => {
          const list = response ?? [];
          list.forEach((g) => {
            this.displayNameMap[g.name] = g.displayName ?? g.name;
          });
        }),
        catchError(() => of([])),
        shareReplay(1)
      );
    }
    return this.load$;
  }

  private resolveDisplayName(): string {
    const token = this.keycloak.tokenParsed as { groups?: string[] } | undefined;
    const groups: string[] = token?.groups ?? [];
    for (const group of groups) {
      const path = group.startsWith('/') ? group : `/${group}`;
      const name = path.slice(1);
      const displayName = this.displayNameMap[name];
      if (displayName) {
        return displayName;
      }
    }
    return '';
  }
}
